import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { listPrices, createPriceEntry, updatePriceEntry, deletePriceEntry, getClients, bulkImportPrices } from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  TrendingUp, Plus, Search, Edit2, Trash2, DollarSign,
  Loader2, Package, Calendar, Hash, Upload, Download,
} from 'lucide-react';

const EMPTY_FORM = {
  product_name: '', item_number: '', client_id: '', supplier_name: '',
  unit_price: '', currency: 'USD', unit: 'pza', quantity: '',
  origin_country: '', notes: '', effective_date: new Date().toISOString().substring(0, 10),
};

export default function PricingPage() {
  const { t } = useI18n();
  const [prices, setPrices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await bulkImportPrices(file);
      const { imported, total_errors, errors } = res.data;
      if (imported > 0) {
        toast.success(`${imported} ${t('pricing.recordsImported')}`);
      }
      if (total_errors > 0) {
        toast.warning(`${total_errors} ${t('pricing.rowsWithErrors')}${errors.length > 0 ? ': ' + errors[0] : ''}`);
      }
      if (imported === 0 && total_errors === 0) {
        toast.info(t('pricing.emptyFile'));
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('pricing.errorImporting'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const headers = 'product_name,item_number,client_name,supplier_name,unit_price,currency,quantity,origin_country,effective_date,notes';
    const example = 'Laptop Dell XPS,SKU-001,Mi Cliente,Proveedor China,1250.00,USD,100,China,2026-01-15,Lote enero';
    const blob = new Blob([headers + '\n' + example + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'precios_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterClient !== 'all') params.client_id = filterClient;
      const [pricesRes, clientsRes] = await Promise.all([
        listPrices(params),
        getClients(),
      ]);
      setPrices(pricesRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(t('pricing.masterAdminOnly'));
      } else {
        toast.error(t('pricing.errorLoading'));
      }
    } finally {
      setLoading(false);
    }
  }, [search, filterClient]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!formData.product_name || !formData.unit_price) {
      toast.error(t('pricing.productRequired'));
      return;
    }
    setSaving(true);
    try {
      const cl = clients.find(c => c.id === formData.client_id);
      const payload = {
        ...formData,
        unit_price: parseFloat(formData.unit_price),
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        client_name: cl?.name || '',
      };
      if (editId) {
        await updatePriceEntry(editId, payload);
        toast.success(t('common.updatedOk'));
      } else {
        await createPriceEntry(payload);
        toast.success(t('pricing.savedOk'));
      }
      setDialogOpen(false);
      setEditId(null);
      setFormData(EMPTY_FORM);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('pricing.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setFormData({
      product_name: entry.product_name || '',
      item_number: entry.item_number || '',
      client_id: entry.client_id || '',
      supplier_name: entry.supplier_name || '',
      unit_price: String(entry.unit_price || ''),
      currency: entry.currency || 'USD',
      unit: entry.unit || 'pza',
      quantity: entry.quantity ? String(entry.quantity) : '',
      origin_country: entry.origin_country || '',
      notes: entry.notes || '',
      effective_date: entry.effective_date?.substring(0, 10) || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('pricing.confirmDelete'))) return;
    try {
      await deletePriceEntry(id);
      toast.success(t('pricing.deletedOk'));
      loadData();
    } catch (err) {
      toast.error(t('pricing.errorDeleting'));
    }
  };

  const openNew = () => {
    setEditId(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const uniqueProducts = [...new Set(prices.map(p => p.product_name))].length;
  const uniqueClients = [...new Set(prices.filter(p => p.client_id).map(p => p.client_id))].length;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="pricing-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading text-foreground flex items-center gap-2">
              <TrendingUp className="text-primary" size={24} />
              {t('pricing.title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('pricing.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleBulkUpload}
              className="hidden"
              data-testid="pricing-csv-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              data-testid="pricing-download-template"
            >
              <Download size={14} className="mr-1" />
              {t('pricing.downloadTemplate')}
            </Button>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="pricing-bulk-upload"
            >
              {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
              {uploading ? t('pricing.uploading') : t('pricing.bulkUpload')}
            </Button>
            <Button onClick={openNew} data-testid="new-price-btn">
              <Plus size={16} className="mr-1" />
              {t('pricing.addEntry')}
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={16} className="text-green-400" />
              <span className="text-xs text-muted-foreground">{t('pricing.totalRecords')}</span>
            </div>
            <p className="text-2xl font-heading">{prices.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Package size={16} className="text-blue-400" />
              <span className="text-xs text-muted-foreground">{t('pricing.products')}</span>
            </div>
            <p className="text-2xl font-heading">{uniqueProducts}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Hash size={16} className="text-purple-400" />
              <span className="text-xs text-muted-foreground">{t('pricing.clientName')}</span>
            </div>
            <p className="text-2xl font-heading">{uniqueClients}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('pricing.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="pricing-search"
            />
          </div>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-44" data-testid="pricing-filter-client">
              <SelectValue placeholder={t('pricing.clientName')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pricing.allClients')}</SelectItem>
              {clients.map(cl => (
                <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : prices.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <TrendingUp size={48} className="mx-auto mb-3 opacity-30" />
            <p>{t('pricing.noPrices')}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left py-2.5 px-3 font-medium">{t('pricing.product')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('pricing.itemNumber')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('pricing.clientName')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('pricing.supplier')}</th>
                    <th className="text-right py-2.5 px-3 font-medium">{t('pricing.unitPrice')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('pricing.currency')}</th>
                    <th className="text-right py-2.5 px-3 font-medium">{t('pricing.quantity')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('pricing.priceDate')}</th>
                    <th className="text-right py-2.5 px-3 font-medium">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((entry, i) => (
                    <tr key={entry.id} className="border-b border-border/50 hover:bg-muted/20" data-testid={`price-row-${i}`}>
                      <td className="py-2.5 px-3 font-medium">{entry.product_name}</td>
                      <td className="py-2.5 px-3 text-muted-foreground">{entry.item_number || '-'}</td>
                      <td className="py-2.5 px-3">{entry.client_name || '-'}</td>
                      <td className="py-2.5 px-3 text-muted-foreground">{entry.supplier_name || '-'}</td>
                      <td className="py-2.5 px-3 text-right font-mono font-medium text-green-400">
                        ${Number(entry.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge variant="outline">{entry.currency}</Badge>
                      </td>
                      <td className="py-2.5 px-3 text-right text-muted-foreground">
                        {entry.quantity ? `${entry.quantity} ${entry.unit}` : '-'}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1">
                          <Calendar size={12} className="text-muted-foreground" />
                          {formatDate(entry.effective_date)}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)} data-testid={`price-edit-${i}`}>
                            <Edit2 size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} data-testid={`price-delete-${i}`}>
                            <Trash2 size={14} className="text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editId ? t('pricing.editPrice') : t('pricing.newPriceEntry')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('pricing.product')} *</Label>
                <Input
                  value={formData.product_name}
                  onChange={e => setFormData(p => ({ ...p, product_name: e.target.value }))}
                  placeholder={t('pricing.product')}
                  data-testid="price-form-product"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('pricing.itemNumber')}</Label>
                  <Input
                    value={formData.item_number}
                    onChange={e => setFormData(p => ({ ...p, item_number: e.target.value }))}
                    placeholder="SKU / Part #"
                    data-testid="price-form-item"
                  />
                </div>
                <div>
                  <Label>{t('pricing.clientName')}</Label>
                  <Select
                    value={formData.client_id || '_none'}
                    onValueChange={v => setFormData(p => ({ ...p, client_id: v === '_none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="price-form-client">
                      <SelectValue placeholder={t('common.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">-</SelectItem>
                      {clients.map(cl => (
                        <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('pricing.supplier')}</Label>
                <Input
                  value={formData.supplier_name}
                  onChange={e => setFormData(p => ({ ...p, supplier_name: e.target.value }))}
                  data-testid="price-form-supplier"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>{t('pricing.unitPrice')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={e => setFormData(p => ({ ...p, unit_price: e.target.value }))}
                    placeholder="0.00"
                    data-testid="price-form-price"
                  />
                </div>
                <div>
                  <Label>{t('pricing.currency')}</Label>
                  <Select value={formData.currency} onValueChange={v => setFormData(p => ({ ...p, currency: v }))}>
                    <SelectTrigger data-testid="price-form-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="MXN">MXN</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="CNY">CNY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('pricing.quantity')}</Label>
                  <Input
                    type="number"
                    value={formData.quantity}
                    onChange={e => setFormData(p => ({ ...p, quantity: e.target.value }))}
                    placeholder="0"
                    data-testid="price-form-qty"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('pricing.originCountry')}</Label>
                  <Input
                    value={formData.origin_country}
                    onChange={e => setFormData(p => ({ ...p, origin_country: e.target.value }))}
                    data-testid="price-form-origin"
                  />
                </div>
                <div>
                  <Label>{t('pricing.effectiveDate')}</Label>
                  <Input
                    type="date"
                    value={formData.effective_date}
                    onChange={e => setFormData(p => ({ ...p, effective_date: e.target.value }))}
                    data-testid="price-form-date"
                  />
                </div>
              </div>
              <div>
                <Label>{t('common.notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  data-testid="price-form-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="price-form-save">
                {saving && <Loader2 size={14} className="mr-1 animate-spin" />}
                {editId ? t('common.update') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
